import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Divider, Typography, Box, IconButton, Avatar,
  useTheme, useMediaQuery, Tooltip, Collapse
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Settings as SettingsIcon,
  ExpandLess, ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Logout
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import { persistor } from '../store';
import logoImage from './assests/cariya-logo.jpeg';

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModules, setOpenModules] = useState({
    partners: false,
    mothers: false,
    donors: false,
    businesses: false,
    reports: false,
    settings: false,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleModuleToggle = (module) => {
    setOpenModules((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      // Purge persisted state to ensure clean logout
      await persistor.purge();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', module: null },
    {
      text: 'Partners', icon: <GroupIcon />, module: 'partners',
      subItems: [
        { text: 'All Partners', path: '/partners' },
        // { text: 'Partner Activities', path: '/partners/activities' },
      ],
    },
    {
      text: 'Mothers', icon: <PeopleIcon />, module: 'mothers',
      subItems: [
        { text: 'All Mothers', path: '/mothers' },
        { text: 'Monthly Savings', path: '/mothers/savings' },
        // { text: 'Compliance', path: '/mothers/compliance' },
      ],
    },
    {
      text: 'Donors', icon: <VolunteerActivismIcon />, module: 'donors',
      subItems: [
        { text: 'Donor Reports', path: '/donors' },
        { text: 'Donor View', path: '/donor-view' },
      ],
    },
    // {
    //   text: 'Reports', icon: <ReportsIcon />, module: 'reports',
    //   subItems: [
    //     { text: 'Savings Trends', path: '/reports/savings' },
    //     { text: 'Donation Trends', path: '/reports/donations' },
    //   ],
    // },
    {
      text: 'Settings', icon: <SettingsIcon />, module: 'settings',
      subItems: [
        { text: 'User Management', path: '/settings/users' },
        { text: 'System Configuration', path: '/settings/config' },
      ],
    },
  ];

  const drawerContent = (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Avatar
          src={logoImage}
          sx={{
            width: isCollapsed ? 40 : 80,
            height: isCollapsed ? 40 : 80,
            mb: 2,
            backgroundColor: '#fff',
            border: `4px solid ${theme.palette.primary.light}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        />
        {!isCollapsed && (
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Cariya Dashboard
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-end', p: 1 }}>
        <IconButton onClick={handleCollapseToggle}>
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <List sx={{ px: isCollapsed ? 0 : 1 }}>
        {menuItems.map((item) => (
          <Box key={item.text}>
            <Tooltip title={item.text} placement="right">
              <ListItem
                button
                onClick={() => {
                  if (item.module) {
                    handleModuleToggle(item.module);
                  } else {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }
                }}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: location.pathname === item.path ? `${theme.palette.primary.main}20` : 'transparent',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? `${theme.palette.primary.main}30` : theme.palette.action.hover,
                    transform: isCollapsed ? 'none' : 'translateX(4px)',
                  },
                  transition: 'all 0.3s ease',
                  py: 1.5,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? theme.palette.primary.main : theme.palette.text.secondary,
                    minWidth: isCollapsed ? 0 : 40,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      color: location.pathname === item.path ? theme.palette.primary.main : theme.palette.text.primary,
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      },
                    }}
                  />
                )}
                {!isCollapsed && item.module && (
                  openModules[item.module] ? <ExpandLess /> : <ExpandMore />
                )}
                {location.pathname === item.path && (
                  <Box
                    sx={{
                      width: 4,
                      height: '100%',
                      borderRadius: 1,
                      backgroundColor: theme.palette.primary.main,
                      position: 'absolute',
                      left: 0,
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>

            {!isCollapsed && item.module && (
              <Collapse in={openModules[item.module]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <Tooltip key={subItem.text} title={subItem.text} placement="right">
                      <ListItem
                        button
                        onClick={() => {
                          navigate(subItem.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                          pl: 4,
                          borderRadius: 2,
                          backgroundColor: location.pathname === subItem.path ? `${theme.palette.primary.main}20` : 'transparent',
                          '&:hover': {
                            backgroundColor: location.pathname === subItem.path ? `${theme.palette.primary.main}30` : theme.palette.action.hover,
                          },
                          transition: 'all 0.3s ease',
                          py: 1,
                        }}
                      >
                        <ListItemText
                          primary={subItem.text}
                          sx={{
                            color: location.pathname === subItem.path ? theme.palette.primary.main : theme.palette.text.primary,
                            '& .MuiTypography-root': {
                              fontSize: isMobile ? '0.85rem' : '0.9rem',
                            },
                          }}
                        />
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      {/* Logout Button */}
      <Box sx={{ mt: 'auto', p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Tooltip title={isCollapsed ? "Logout" : ""} placement="right" arrow>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: isCollapsed ? 1.5 : 2,
                color: '#F44336',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  transform: isCollapsed ? 'none' : 'translateX(4px)',
                },
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                minHeight: 48,
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: '#F44336',
                  minWidth: isCollapsed ? 0 : 40,
                  mr: isCollapsed ? 0 : 2,
                }}
              >
                <Logout />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    letterSpacing: '0.2px',
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
        
        {!isCollapsed && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            © {new Date().getFullYear()} Cariya Wallet. All Rights Reserved.
          </Typography>
        )}
      </Box>
    </>
  );

  return (
    <>
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          <HomeIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? 280 : isCollapsed ? 80 : 280,
            boxSizing: 'border-box',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: 'none',
            background: '#fff',
            transition: 'width 0.3s ease',
          },
        }}
      >
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={handleDrawerToggle}>
              <HomeIcon />
            </IconButton>
          </Box>
        )}
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;